import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { api } from './api';
import type { Profile } from './types';

type AuthValue = {
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
  hasRole: (...roles: string[]) => boolean;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshProfile = async () => {
    const current = (await supabase.auth.getSession()).data.session;
    if (!current) { setProfile(null); return; }
    setProfile(await api<Profile>('/api/me'));
  };

  useEffect(() => {
    let alive = true;
    supabase.auth.getSession().then(async ({ data }) => {
      if (!alive) return;
      setSession(data.session);
      if (data.session) {
        try { setProfile(await api<Profile>('/api/me')); } catch { setProfile(null); }
      }
      setLoading(false);
    });
    const { data: subscription } = supabase.auth.onAuthStateChange(async (_event, next) => {
      setSession(next);
      if (next) {
        try { setProfile(await api<Profile>('/api/me')); } catch { setProfile(null); }
      } else setProfile(null);
      setLoading(false);
    });
    return () => { alive = false; subscription.subscription.unsubscribe(); };
  }, []);

  const value = useMemo<AuthValue>(() => ({
    session,
    profile,
    loading,
    refreshProfile,
    signOut: async () => { await supabase.auth.signOut(); },
    hasRole: (...roles) => !!profile?.roles.some((role) => roles.includes(role.code)),
  }), [session, profile, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used within AuthProvider');
  return value;
}
